<template>
  <div>
    <helpers-section-header class="mt-6 mb-2"> Fundamental Signal Rules </helpers-section-header>
    <p class="text-body-2 mb-3">
      Filter stock selections using financial statement data — valuation ratios, profitability
      metrics, growth rates, leverage, and more. Rules are organized into <strong>groups</strong>.
      Within each group you choose whether all metrics must pass (AND) or any one is sufficient
      (OR). When set to Filter mode in the signal list, this signal gates what other signals can
      select — for example, requiring a minimum Piotroski Score or positive free cash flow before
      acting on a 13F or insider signal. Create a rule group first, then add individual metric
      thresholds to it.
    </p>
    <v-row density="compact">
      <v-col cols="12">
        <v-data-table
          :headers="group_headers"
          :items="criteria.rules"
          hide-default-footer
          items-per-page="-1"
          class="mt-6 mb-8"
        >
          <template #top>
            <v-dialog v-model="groupDialog" fullscreen>
              <template #activator="{ props }">
                <v-btn
                  color="primary"
                  theme="dark"
                  style="max-width: 350px"
                  class="mb-4"
                  v-bind="props"
                >
                  Create New Rule Group
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="text-h5">{{ groupTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-row density="compact">
                    <v-col cols="12" sm="12">
                      <v-checkbox
                        v-model="editedGroup.enabled"
                        density="compact"
                        label="Rule Enabled"
                      />
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <v-select
                        v-model="editedGroup.and_or"
                        :items="[
                          { id: 1, description: 'All rules must match for a stock to be chosen' },
                          {
                            id: 2,
                            description: 'If any rule matches then the stock will be chosen'
                          }
                        ]"
                        item-title="description"
                        item-value="id"
                        label="How are stocks matched?"
                        density="compact"
                        hide-details
                        variant="outlined"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-data-table
                        :headers="metric_headers"
                        :items="editedGroup.metric_rules"
                        hide-default-footer
                        items-per-page="-1"
                      >
                        <template #top>
                          <v-dialog v-model="metricDialog" persistent scrollable max-width="650px">
                            <template #activator="{ props }">
                              <v-btn
                                color="primary"
                                style="max-width: 350px"
                                class="mb-4"
                                v-bind="props"
                              >
                                Add Fundamental To Group
                              </v-btn>
                            </template>
                            <v-card>
                              <v-card-title>
                                <span class="text-h5">{{ metricTitle }}</span>
                              </v-card-title>

                              <v-card-text style="height: 350px">
                                <p />
                                <v-container>
                                  <v-row density="compact">
                                    <v-col cols="12" sm="12">
                                      <v-checkbox
                                        v-model="editedMetric.enabled"
                                        density="compact"
                                        label="Rule Enabled"
                                      />
                                    </v-col>
                                    <v-col cols="12" sm="12">
                                      <v-autocomplete
                                        v-model="editedMetric.metric"
                                        :items="metrics"
                                        placeholder=""
                                        label="Fundamental"
                                        item-title="name"
                                        item-value="id"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                      />
                                    </v-col>
                                    <v-col cols="12" sm="12">
                                      <v-row density="compact">
                                        <v-col cols="12" sm="12" md="4">
                                          <v-select
                                            v-model="editedMetric.comparison"
                                            :items="comparison_options"
                                            label="Restrict"
                                            item-title="description"
                                            item-value="id"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                          />
                                        </v-col>
                                        <v-col cols="12" sm="12" md="4">
                                          <v-text-field
                                            v-if="
                                              editedMetric.comparison == 1 ||
                                              editedMetric.comparison == 3
                                            "
                                            v-model="editedMetric.min"
                                            density="compact"
                                            hide-details
                                            variant="outlined"
                                            label="Enter Min."
                                          />
                                          <v-text-field
                                            v-if="editedMetric.comparison == 2"
                                            v-model="editedMetric.max"
                                            density="compact"
                                            hide-details
                                            variant="outlined"
                                            label="Enter Max."
                                          />
                                        </v-col>
                                        <v-col cols="12" sm="12" md="4">
                                          <v-text-field
                                            v-if="editedMetric.comparison == 3"
                                            v-model="editedMetric.max"
                                            density="compact"
                                            hide-details
                                            variant="outlined"
                                            label="Enter Max."
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-container>
                              </v-card-text>

                              <v-card-actions>
                                <div class="flex-grow-1" />
                                <v-btn color="blue-darken-1" text @click="closeMetric">
                                  Cancel
                                </v-btn>
                                <v-btn color="blue-darken-1" text @click="saveMetric"> Save </v-btn>
                              </v-card-actions>
                            </v-card>
                          </v-dialog>
                        </template>
                        <template #item.metric_description="{ item }">
                          {{ LookupMetric(item.metric) }}
                          <div v-if="item.comparison == 1">is greater than {{ item.min }}</div>
                          <div v-else-if="item.comparison == 2">
                            is less than or equal to {{ item.max }}
                          </div>
                          <div v-else-if="item.comparison == 3">
                            is between {{ item.min }} and {{ item.max }}
                          </div>
                        </template>
                        <template #item.action="{ item }">
                          <v-icon small class="mr-2" @click="editMetric(item)">
                            {{ mdiPencil }}
                          </v-icon>
                          <v-icon small @click="deleteMetric(item)">
                            {{ mdiDelete }}
                          </v-icon>
                        </template>

                        <template #item.enabled="{ item }">
                          <v-switch v-model="item.enabled" color="primary" />
                        </template>
                        <template #no-data> No fundamental metrics configured </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" variant="outlined" @click="closeGroup"> Cancel </v-btn>
                  <v-btn color="primary" variant="outlined" @click="saveGroup"> Save </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          <template #item.group_description="{ item }">
            <span v-if="item.and_or == 1">All rules must match</span>
            <span v-else-if="item.and_or == 2">Any rule matches</span>
            <ul>
              <li v-for="rule in item.metric_rules" :key="rule.metric">
                <span v-if="rule.enabled">Enabled: </span>
                <span v-else>Not Enabled: </span>
                {{ LookupMetric(rule.metric) }}
                <div v-if="rule.comparison == 1">is greater than {{ rule.min }}</div>
                <div v-else-if="rule.comparison == 2">is less than or equal to {{ rule.max }}</div>
                <div v-else-if="rule.comparison == 3">
                  is between {{ rule.min }} and {{ rule.max }}
                </div>
              </li>
            </ul>
          </template>
          <template #item.action="{ item }">
            <v-icon small class="mr-2" @click="editGroup(item)">
              {{ mdiPencil }}
            </v-icon>
            <v-icon small @click="deleteGroup(item)">
              {{ mdiDelete }}
            </v-icon>
          </template>

          <template #item.enabled="{ item }">
            <v-switch v-model="item.enabled" color="primary" />
          </template>
          <template #no-data> No Rule Groups configured </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// Modules
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import { mdiDelete, mdiPencil } from '@mdi/js'
export default {
  data() {
    return {
      mdiDelete,
      mdiPencil,
      metricDialog: false,
      groupDialog: false,
      comparison_options: [
        { id: 1, description: 'Greater Than' },
        { id: 2, description: 'Less Than or equal to' },
        { id: 3, description: 'Between' }
      ],
      defaultRule: { enabled: true, and_or: 1, metric_rules: [] },
      defaultMetricRule: { metric: null, enabled: true, min: null, max: null, comparison: 1 },
      metrics: [
        { id: 'piotroski', name: 'Piotroski Score', description: '', enabled: true },
        {
          id: 'percent_change_other_equity',
          name: '% Change in Other Equity TTM',
          description: '',
          enabled: true
        },
        {
          id: 'percent_change_shares_outstanding',
          name: '% Change in Shares Outstanding',
          description: '',
          enabled: true
        },
        {
          id: 'issuanceofcommonequity',
          name: 'Issuance of Common Equity TTM Cash Flow',
          description: '',
          enabled: true
        },
        {
          id: 'otherequity',
          name: 'Other Equity (warrants and stock options) Balance Sheet',
          description: '',
          enabled: true
        },
        { id: 'revenuegrowth', name: 'Revenue Growth', description: '', enabled: true },
        {
          id: 'nopat',
          name: 'Net Operating Profit after Tax (NOPAT)',
          description: '',
          enabled: true
        },
        { id: 'nopatmargin', name: 'NOPAT Margin', description: '', enabled: true },
        { id: 'investedcapital', name: 'Invested Capital', description: '', enabled: true },
        {
          id: 'investedcapitalturnover',
          name: 'Invested Capital Turnover',
          description: '',
          enabled: true
        },
        {
          id: 'investedcapitalincreasedecrease',
          name: 'Increase / (Decrease) in Invested Capital',
          description: '',
          enabled: true
        },
        {
          id: 'freecashflow',
          name: 'Free Cash Flow to Firm (FCFF)',
          description: '',
          enabled: true
        },
        {
          id: 'netnonopex',
          name: 'Net Nonoperating Expense (NNE)',
          description: '',
          enabled: true
        },
        {
          id: 'netnonopobligations',
          name: 'Net Nonoperating Obligations (NNO)',
          description: '',
          enabled: true
        },
        {
          id: 'ebit',
          name: 'Earnings before Interest and Taxes (EBIT)',
          description: '',
          enabled: true
        },
        {
          id: 'depreciationandamortization',
          name: 'Total Depreciation and Amortization (D&A)',
          description: '',
          enabled: true
        },
        {
          id: 'ebitda',
          name: 'Earnings before Interest, Taxes, Depreciation and Amortization (EBITDA)',
          description: '',
          enabled: true
        },
        { id: 'capex', name: 'Capital Expenditures (CapEx)', description: '', enabled: true },
        {
          id: 'dfcfnwc',
          name: 'Debt-free, Cash-free Net Working Capital (DFCFNWC)',
          description: '',
          enabled: true
        },
        {
          id: 'dfnwc',
          name: 'Debt-free Net Working Capital (DFNWC)',
          description: '',
          enabled: true
        },
        { id: 'nwc', name: 'Net Working Capital (NWC)', description: '', enabled: true },
        { id: 'debt', name: 'Total Debt', description: '', enabled: true },
        { id: 'ltdebtandcapleases', name: 'Total Long-Term Debt', description: '', enabled: true },
        { id: 'netdebt', name: 'Net Debt', description: '', enabled: true },
        { id: 'totalcapital', name: 'Total Capital', description: '', enabled: true },
        { id: 'bookvaluepershare', name: 'Book Value per Share', description: '', enabled: true },
        {
          id: 'tangbookvaluepershare',
          name: 'Tangible Book Value per Share',
          description: '',
          enabled: true
        },
        { id: 'marketcap', name: 'Market Capitalization', description: '', enabled: true },
        { id: 'enterprisevalue', name: 'Enterprise Value (EV)', description: '', enabled: true },
        { id: 'pricetobook', name: 'Price to Book Value (P/BV)', description: '', enabled: true },
        {
          id: 'pricetotangiblebook',
          name: 'Price to Tangible Book Value (P/TBV)',
          description: '',
          enabled: true
        },
        { id: 'pricetorevenue', name: 'Price to Revenue (P/Rev)', description: '', enabled: true },
        { id: 'pricetoearnings', name: 'Price to Earnings (P/E)', description: '', enabled: true },
        { id: 'dividendyield', name: 'Dividend Yield', description: '', enabled: true },
        { id: 'earningsyield', name: 'Earnings Yield', description: '', enabled: true },
        {
          id: 'evtoinvestedcapital',
          name: 'Enterprise Value to Invested Capital (EV/IC)',
          description: '',
          enabled: true
        },
        {
          id: 'evtorevenue',
          name: 'Enterprise Value to Revenue (EV/Rev)',
          description: '',
          enabled: true
        },
        {
          id: 'evtoebitda',
          name: 'Enterprise Value to EBITDA (EV/EBITDA)',
          description: '',
          enabled: true
        },
        {
          id: 'evtoebit',
          name: 'Enterprise Value to EBIT (EV/EBIT)',
          description: '',
          enabled: true
        },
        {
          id: 'evtonopat',
          name: 'Enterprise Value to NOPAT (EV/NOPAT)',
          description: '',
          enabled: true
        },
        {
          id: 'evtoocf',
          name: 'Enterprise Value to Operating Cash Flow (EV/OCF)',
          description: '',
          enabled: true
        },
        {
          id: 'evtofcff',
          name: 'Enterprise Value to Free Cash Flow (EV/FCFF)',
          description: '',
          enabled: true
        },
        { id: 'ebitdagrowth', name: 'EBITDA Growth', description: '', enabled: true },
        { id: 'ebitgrowth', name: 'EBIT Growth', description: '', enabled: true },
        { id: 'nopatgrowth', name: 'NOPAT Growth', description: '', enabled: true },
        { id: 'netincomegrowth', name: 'Net Income Growth', description: '', enabled: true },
        { id: 'epsgrowth', name: 'EPS Growth', description: '', enabled: true },
        { id: 'ocfgrowth', name: 'Operating Cash Flow Growth', description: '', enabled: true },
        { id: 'fcffgrowth', name: 'Free Cash Flow Firm Growth', description: '', enabled: true },
        {
          id: 'investedcapitalgrowth',
          name: 'Invested Capital Growth',
          description: '',
          enabled: true
        },
        { id: 'revenueqoqgrowth', name: 'Revenue Q/Q Growth', description: '', enabled: true },
        { id: 'ebitdaqoqgrowth', name: 'EBITDA Q/Q Growth', description: '', enabled: true },
        { id: 'ebitqoqgrowth', name: 'EBIT Q/Q Growth', description: '', enabled: true },
        { id: 'nopatqoqgrowth', name: 'NOPAT Q/Q Growth', description: '', enabled: true },
        { id: 'netincomeqoqgrowth', name: 'Net Income Q/Q Growth', description: '', enabled: true },
        { id: 'epsqoqgrowth', name: 'EPS Q/Q Growth', description: '', enabled: true },
        {
          id: 'ocfqoqgrowth',
          name: 'Operating Cash Flow Q/Q Growth',
          description: '',
          enabled: true
        },
        {
          id: 'fcffqoqgrowth',
          name: 'Free Cash Flow Firm Q/Q Growth',
          description: '',
          enabled: true
        },
        {
          id: 'investedcapitalqoqgrowth',
          name: 'Invested Capital Q/Q Growth',
          description: '',
          enabled: true
        },
        { id: 'grossmargin', name: 'Gross Margin', description: '', enabled: true },
        { id: 'ebitdamargin', name: 'EBITDA Margin', description: '', enabled: true },
        { id: 'operatingmargin', name: 'Operating Margin', description: '', enabled: true },
        { id: 'ebitmargin', name: 'EBIT Margin', description: '', enabled: true },
        { id: 'profitmargin', name: 'Profit (Net Income) Margin', description: '', enabled: true },
        {
          id: 'costofrevtorevenue',
          name: 'Cost of Revenue to Revenue',
          description: '',
          enabled: true
        },
        { id: 'sgaextorevenue', name: 'SG&A Expenses to Revenue', description: '', enabled: true },
        { id: 'rdextorevenue', name: 'R&D to Revenue', description: '', enabled: true },
        {
          id: 'opextorevenue',
          name: 'Operating Expenses to Revenue',
          description: '',
          enabled: true
        },
        { id: 'taxburdenpct', name: 'Tax Burden Percent', description: '', enabled: true },
        {
          id: 'interestburdenpct',
          name: 'Interest Burden Percent',
          description: '',
          enabled: true
        },
        { id: 'efftaxrate', name: 'Effective Tax Rate', description: '', enabled: true },
        { id: 'assetturnover', name: 'Asset Turnover', description: '', enabled: true },
        { id: 'arturnover', name: 'Accounts Receivable Turnover', description: '', enabled: true },
        { id: 'invturnover', name: 'Inventory Turnover', description: '', enabled: true },
        { id: 'faturnover', name: 'Fixed Asset Turnover', description: '', enabled: true },
        { id: 'apturnover', name: 'Accounts Payable Turnover', description: '', enabled: true },
        { id: 'dso', name: 'Days Sales Outstanding (DSO)', description: '', enabled: true },
        { id: 'dio', name: 'Days Inventory Outstanding (DIO)', description: '', enabled: true },
        { id: 'dpo', name: 'Days Payable Outstanding (DPO)', description: '', enabled: true },
        { id: 'ccc', name: 'Cash Conversion Cycle (CCC)', description: '', enabled: true },
        { id: 'finleverage', name: 'Financial Leverage', description: '', enabled: true },
        { id: 'leverageratio', name: 'Leverage Ratio', description: '', enabled: true },
        {
          id: 'compoundleveragefactor',
          name: 'Compound Leverage Factor',
          description: '',
          enabled: true
        },
        { id: 'ltdebttoequity', name: 'Long-Term Debt to Equity', description: '', enabled: true },
        { id: 'debttoequity', name: 'Debt to Equity', description: '', enabled: true },
        { id: 'roic', name: 'Return on Invested Capital (ROIC)', description: '', enabled: true },
        {
          id: 'nnep',
          name: 'Net Nonoperating Expense Percent (NNEP)',
          description: '',
          enabled: true
        },
        {
          id: 'roicnnepspread',
          name: 'ROIC Less NNEP Spread (ROIC-NNEP)',
          description: '',
          enabled: true
        },
        {
          id: 'rnnoa',
          name: 'Return on Net Nonoperating Assets (RNNOA)',
          description: '',
          enabled: true
        },
        { id: 'roe', name: 'Return on Equity (ROE)', description: '', enabled: true },
        {
          id: 'croic',
          name: 'Cash Return on Invested Capital (CROIC)',
          description: '',
          enabled: true
        },
        { id: 'oroa', name: 'Operating Return on Assets (OROA)', description: '', enabled: true },
        { id: 'roa', name: 'Return on Assets (ROA)', description: '', enabled: true },
        {
          id: 'noncontrollinginterestsharingratio',
          name: 'Noncontrolling Interest Sharing Ratio',
          description: '',
          enabled: true
        },
        { id: 'roce', name: 'Return on Common Equity (ROCE)', description: '', enabled: true },
        { id: 'divpayoutratio', name: 'Dividend Payout Ratio', description: '', enabled: true },
        {
          id: 'augmentedpayoutratio',
          name: 'Augmented Payout Ratio',
          description: '',
          enabled: true
        },
        { id: 'ocftocapex', name: 'Operating Cash Flow to CapEx', description: '', enabled: true },
        {
          id: 'stdebttocap',
          name: 'Short-Term Debt to Total Capital',
          description: '',
          enabled: true
        },
        {
          id: 'ltdebttocap',
          name: 'Long-Term Debt to Total Capital',
          description: '',
          enabled: true
        },
        { id: 'debttototalcapital', name: 'Debt to Total Capital', description: '', enabled: true },
        {
          id: 'preferredtocap',
          name: 'Preferred Equity to Total Capital',
          description: '',
          enabled: true
        },
        {
          id: 'noncontrolinttocap',
          name: 'Noncontrolling Interests to Total Capital',
          description: '',
          enabled: true
        },
        {
          id: 'commontocap',
          name: 'Common Equity to Total Capital',
          description: '',
          enabled: true
        },
        { id: 'debttoebitda', name: 'Debt to EBITDA', description: '', enabled: true },
        { id: 'netdebttoebitda', name: 'Net Debt to EBITDA', description: '', enabled: true },
        { id: 'ltdebttoebitda', name: 'Long-Term Debt to EBITDA', description: '', enabled: true },
        { id: 'debttonopat', name: 'Debt to NOPAT', description: '', enabled: true },
        { id: 'netdebttonopat', name: 'Net Debt to NOPAT', description: '', enabled: true },
        { id: 'ltdebttonopat', name: 'Long-Term Debt to NOPAT', description: '', enabled: true },
        { id: 'altmanzscore', name: 'Altman Z-Score', description: '', enabled: true },
        {
          id: 'ebittointerestex',
          name: 'EBIT to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'nopattointerestex',
          name: 'NOPAT to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'ebitlesscapextointerestex',
          name: 'EBIT Less CapEx to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'nopatlesscapextointex',
          name: 'NOPAT Less CapEx to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'ocftointerestex',
          name: 'Operating Cash Flow to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'ocflesscapextointerestex',
          name: 'Operating Cash Flow Less CapEx to Interest Expense',
          description: '',
          enabled: true
        },
        {
          id: 'fcfftointerestex',
          name: 'Free Cash Flow to Firm to Interest Expense',
          description: '',
          enabled: true
        },
        { id: 'currentratio', name: 'Current Ratio', description: '', enabled: true },
        { id: 'quickratio', name: 'Quick Ratio', description: '', enabled: true },
        {
          id: 'dfcfnwctorev',
          name: 'Debt-free, Cash-free Net Working Capital to Revenue',
          description: '',
          enabled: true
        },
        {
          id: 'dfnwctorev',
          name: 'Debt-free Net Working Capital to Revenue',
          description: '',
          enabled: true
        },
        { id: 'nwctorev', name: 'Net Working Capital to Revenue', description: '', enabled: true },
        {
          id: 'normalizednopat',
          name: 'Normalized Net Operating Profit after Tax (NOPAT)',
          description: '',
          enabled: true
        },
        {
          id: 'normalizednopatmargin',
          name: 'Normalized NOPAT Margin',
          description: '',
          enabled: true
        },
        { id: 'pretaxincomemargin', name: 'Pre Tax Income Margin', description: '', enabled: true },
        {
          id: 'adjweightedavebasicsharesos',
          name: 'Adjusted Weighted Average Basic Shares Outstanding',
          description: '',
          enabled: true
        },
        {
          id: 'adjbasiceps',
          name: 'Adjusted Basic Earnings per Share',
          description: '',
          enabled: true
        },
        {
          id: 'adjweightedavedilutedsharesos',
          name: 'Adjusted Weighted Average Diluted Shares Outstanding',
          description: '',
          enabled: true
        },
        {
          id: 'adjdilutedeps',
          name: 'Adjusted Diluted Earnings per Share',
          description: '',
          enabled: true
        },
        {
          id: 'adjweightedavebasicdilutedsharesos',
          name: 'Adjusted Weighted Average Basic & Diluted Shares Outstanding',
          description: '',
          enabled: true
        },
        {
          id: 'adjbasicdilutedeps',
          name: 'Adjusted Basic & Diluted Earnings per Share',
          description: '',
          enabled: true
        },
        {
          id: 'roe_simple',
          name: 'Return on Equity Simple (ROE_SIMPLE)',
          description: '',
          enabled: true
        }
      ],
      group_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Description',
          align: 'left',
          sortable: false,
          key: 'group_description'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      metric_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Description',
          align: 'left',
          sortable: false,
          key: 'metric_description'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      editedGroup: { enabled: true, and_or: 1, metric_rules: [] },
      editedIndex: -1,
      editedMetric: { metric: null, enabled: true, min: null, max: null, comparison: null },
      editedMetricIndex: -1
    }
  },
  computed: {
    groupTitle() {
      return this.editedIndex === -1 ? 'New Rule Group' : 'Edit Rule Group'
    },
    metricTitle() {
      return this.editedIndex === -1 ? 'New Fundamental Rule' : 'Edit Fundamental Rule'
    },
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.signals.fundamental_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.fundamental_signal.enabled
    }
    /* ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.fundamental_signal',
      enabled: 'app.whaleTraderCriteria.signals.fundamental_signal.enabled',
      signalView: 'app.signalView'
    })*/
  },
  methods: {
    LookupMetric(metric) {
      var desc = ''
      for (var i = 0; i < this.metrics.length; i++) {
        var m = this.metrics[i]
        if (m.id == metric) {
          desc = m.name
          break
        }
      }
      return desc
    },
    editGroup(item) {
      this.editedIndex = this.criteria.rules.indexOf(item)
      this.editedGroup = Object.assign({}, item)
      this.groupDialog = true
    },

    deleteGroup(item) {
      const index = this.criteria.rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') && this.criteria.rules.splice(index, 1)
    },

    closeGroup() {
      this.groupDialog = false
      this.editedGroup = cloneDeep(this.defaultRule) // Object.assign({}, this.defaultRule)
      this.editedIndex = -1
    },

    saveGroup() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.rules[this.editedIndex], this.editedGroup)
      } else {
        if (!this.criteria.rules) {
          this.criteria.rules = []
        }
        this.criteria.rules.push(this.editedGroup)
      }
      this.closeGroup()
    },

    editMetric(item) {
      this.editedMetricIndex = this.editedGroup.metric_rules.indexOf(item)
      this.editedMetric = Object.assign({}, item)
      this.metricDialog = true
    },

    deleteMetric(item) {
      const index = this.editedGroup.metric_rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.editedGroup.metric_rules.splice(index, 1)
    },

    closeMetric() {
      this.metricDialog = false
      setTimeout(() => {
        this.editedMetric = Object.assign({}, this.defaultMetricRule)
        this.editedMetricIndex = -1
      }, 300)
    },

    saveMetric() {
      if (this.editedMetricIndex > -1) {
        Object.assign(this.editedGroup.metric_rules[this.editedMetricIndex], this.editedMetric)
      } else {
        if (!this.editedGroup.metric_rules) {
          this.editedGroup.metric_rules = []
        }
        this.editedGroup.metric_rules.push(this.editedMetric)
      }
      this.closeMetric()
    }
  }
}
</script>
